import {
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Globe,
  MapPin,
  Phone,
  User,
  Users,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { certificateTranslationKey } from "@/lib/certificates"
import type { Cooperative } from "@/types/domain"

interface CooperativeInfoCardProps {
  cooperative: Cooperative
}

export function CooperativeInfoCard({ cooperative }: CooperativeInfoCardProps) {
  const { t } = useTranslation(["cooperatives", "common"])
  const [open, setOpen] = useState(true)

  const memberCount = cooperative.memberCount ?? 0
  const certifications = cooperative.certifications ?? []

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-base font-semibold">
          <Building2 className="size-5 text-muted-foreground" />
          {t("info.title")}
        </span>
        {open ? (
          <ChevronUp className="size-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-4 text-muted-foreground" />
        )}
      </button>

      {open ? (
        <>
          <Separator />
          <CardContent className="grid gap-6 p-6 sm:grid-cols-2">
            <Section title={t("info.section.basics")}>
              <InfoRow icon={MapPin} text={cooperative.address ?? "—"} />
              <InfoRow icon={Globe} text={cooperative.region ?? "—"} />
              <InfoRow
                icon={Users}
                text={t("info.fields.members", { count: memberCount })}
              />
              {cooperative.yearFounded ? (
                <InfoRow
                  icon={Calendar}
                  text={t("info.fields.founded", {
                    year: cooperative.yearFounded,
                  })}
                />
              ) : null}
            </Section>

            <Section title={t("info.section.contact")}>
              <InfoRow icon={User} text={cooperative.contactPerson ?? "—"} />
              {cooperative.contactPhone ? (
                <InfoRow
                  icon={Phone}
                  text={
                    <a
                      href={`tel:${cooperative.contactPhone}`}
                      className="text-primary hover:underline"
                    >
                      {cooperative.contactPhone}
                    </a>
                  }
                />
              ) : (
                <InfoRow icon={Phone} text="—" />
              )}
            </Section>

            <Section
              title={t("info.section.certifications")}
              className="sm:col-span-2"
            >
              {certifications.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {certifications.map((cert) => {
                    const key = certificateTranslationKey(cert)
                    const label = key
                      ? t(key.replace("common:", ""), { ns: "common" })
                      : cert
                    return (
                      <Badge
                        key={cert}
                        variant="outline"
                        className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
                      >
                        {label}
                      </Badge>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("info.fields.noCertifications")}
                </p>
              )}
            </Section>

            <Section
              title={t("info.section.description")}
              className="sm:col-span-2"
            >
              <p className="text-sm leading-relaxed text-muted-foreground">
                {cooperative.description?.trim() ||
                  t("info.fields.noDescription")}
              </p>
            </Section>
          </CardContent>
        </>
      ) : null}
    </Card>
  )
}

function Section({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={className}>
      <h3 className="mb-3 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {title}
      </h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  )
}

function InfoRow({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>
  text: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="text-foreground">{text}</span>
    </div>
  )
}
